/**
 * Schedule Excel Dialog
 *
 * Provides Excel data management for schedules:
 * - Download schedule data as Excel file
 * - Upload Excel data with confirmation and validation
 *
 * Smart download behavior:
 * - Populated schedule → downloads schedule data
 * - Empty schedule → downloads blank template
 */

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Alert,
  Typography,
  Snackbar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import TableChartIcon from '@mui/icons-material/TableChart';
import ClassIcon from '@mui/icons-material/Class';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import { API_BASE_URL, API_VERSION } from '../../../config/api';
import { getCsrfToken } from '../../../utils/sessionAuth';

interface ScheduleExcelDialogProps {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  studioId: string;
  scheduleName: string;
  classCount: number;
  roomCount: number;
  teacherCount: number;
  onUploadSuccess?: () => void;
}

interface ValidationError {
  sheet: string;
  row: number;
  field: string;
  message: string;
}

const ScheduleExcelDialog: React.FC<ScheduleExcelDialogProps> = ({
  open,
  onClose,
  scheduleId,
  studioId,
  scheduleName,
  classCount,
  roomCount,
  teacherCount,
  onUploadSuccess,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<ValidationError[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasData = classCount > 0 || roomCount > 0 || teacherCount > 0;

  const showToast = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDownloadExcel = async () => {
    setIsDownloading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/${API_VERSION}/scheduling/schedules/${scheduleId}/download_excel/?studio=${studioId}`,
        {
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${scheduleName.replace(/\s+/g, '_')}_schedule.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      showToast('Schedule downloaded successfully!', 'success');
    } catch (err) {
      console.error('Failed to download schedule:', err);
      setError('Failed to download schedule. Please try again.');
      showToast('Failed to download schedule. Please try again.', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setUploadErrors([]);
    setSelectedFile(null);
    onClose();
  };

  const getButtonLabel = () => {
    if (isDownloading) return 'Downloading...';
    if (hasData) return 'Download Schedule Data';
    return 'Download Blank Template';
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError(
          'Invalid file type. Please select an Excel file (.xlsx or .xls)'
        );
        showToast('Invalid file type. Please select an Excel file.', 'error');
        return;
      }
      setSelectedFile(file);
      setUploadErrors([]);
      setError(null);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setError(
          'Invalid file type. Please select an Excel file (.xlsx or .xls)'
        );
        showToast('Invalid file type. Please select an Excel file.', 'error');
        return;
      }
      setSelectedFile(file);
      setUploadErrors([]);
      setError(null);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleConfirmUpload = async () => {
    setConfirmDialogOpen(false);

    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('schedule', scheduleId);

      const csrfToken = getCsrfToken();

      const response = await fetch(
        `${API_BASE_URL}/api/${API_VERSION}/scheduling/uploads/upload/?studio=${studioId}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRFToken': csrfToken || '',
          },
          body: formData,
        }
      );

      const result = await response.json();

      if (result.status === 'success') {
        const { summary } = result;
        const summaryMessage = `Successfully imported: ${summary.classes_created} classes, ${summary.rooms_created} rooms, ${summary.teachers_created} teachers`;
        showToast(summaryMessage, 'success');
        setSelectedFile(null);

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        if (result.errors && result.errors.length > 0) {
          setUploadErrors(result.errors);
          setError(
            `Upload failed: ${result.message || 'Validation errors found'}`
          );
        } else {
          setError(result.message || 'Upload failed');
        }
        showToast(
          result.message || 'Upload failed. Please check errors.',
          'error'
        );
      }
    } catch (err) {
      console.error('Failed to upload schedule:', err);
      setError('Failed to upload schedule. Please try again.');
      showToast('Failed to upload schedule. Please try again.', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (hasData) {
      setConfirmDialogOpen(true);
    } else {
      handleConfirmUpload();
    }
  };

  const handleCancelUpload = () => {
    setConfirmDialogOpen(false);
  };

  const getErrorsBySheet = () => {
    const grouped: { [key: string]: ValidationError[] } = {};
    uploadErrors.forEach(validationError => {
      if (!grouped[validationError.sheet]) {
        grouped[validationError.sheet] = [];
      }
      grouped[validationError.sheet].push(validationError);
    });
    return grouped;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '400px',
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <TableChartIcon />
            <Typography variant="h6">Schedule Data Management</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Download Section */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <DownloadIcon color="primary" />
              <Typography variant="h6">Download Schedule Data</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {hasData
                ? 'Download your schedule data as an Excel file. You can edit the data offline and re-upload it later.'
                : 'Download a blank Excel template to create your schedule data.'}
            </Typography>

            {hasData ? (
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ClassIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${classCount} classes`}
                    secondary="Dance class definitions"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <MeetingRoomIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${roomCount} rooms`}
                    secondary="Studio room configurations"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${teacherCount} teachers`}
                    secondary="Instructor information"
                  />
                </ListItem>
              </List>
            ) : (
              <Alert severity="info">
                This schedule is empty. Download will provide a blank template
                with proper formatting and column headers.
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={
                isDownloading ? (
                  <CircularProgress size={20} />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleDownloadExcel}
              disabled={isDownloading}
              sx={{ mt: 2 }}
            >
              {getButtonLabel()}
            </Button>
          </Paper>

          <Divider sx={{ my: 3 }} />

          {/* Upload Section */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CloudUploadIcon color="primary" />
              <Typography variant="h6">Upload Schedule Data</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload an Excel file to import or update your schedule data.
            </Typography>

            {/* File Drop Zone */}
            <Box
              onClick={handleFileClick}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              sx={{
                border: '2px dashed',
                borderColor: selectedFile ? 'success.main' : 'grey.300',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: selectedFile ? 'success.50' : 'grey.50',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'primary.50',
                },
                mb: 2,
              }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />

              {selectedFile ? (
                <Box>
                  <CheckCircleIcon
                    sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                  />
                  <Typography variant="body1" fontWeight="bold">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click to select a different file
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadFileIcon
                    sx={{ fontSize: 48, color: 'grey.400', mb: 1 }}
                  />
                  <Typography variant="body1" fontWeight="bold">
                    Drag & drop Excel file here
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports .xlsx and .xls files
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Warning if data exists */}
            {hasData && selectedFile && (
              <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Uploading will replace all existing
                  data ({classCount} classes, {roomCount} rooms, {teacherCount}{' '}
                  teachers). This action cannot be undone.
                </Typography>
              </Alert>
            )}

            {/* Validation Errors */}
            {uploadErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Validation Errors Found ({uploadErrors.length} total):
                </Typography>
                {Object.entries(getErrorsBySheet()).map(
                  ([sheet, sheetErrors]) => (
                    <Accordion key={sheet} sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ErrorIcon fontSize="small" color="error" />
                          <Typography variant="body2" fontWeight="bold">
                            {sheet} ({sheetErrors.length} errors)
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <List dense>
                          {sheetErrors.map(validationError => (
                            <ListItem
                              key={`${sheet}-${validationError.row}-${validationError.field}-${validationError.message}`}
                            >
                              <ListItemText
                                primary={`Row ${validationError.row}: ${validationError.field}`}
                                secondary={validationError.message}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </Alert>
            )}

            {/* Upload Button */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={
                isUploading ? (
                  <CircularProgress size={20} />
                ) : (
                  <CloudUploadIcon />
                )
              }
              onClick={handleUploadClick}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload Schedule Data'}
            </Button>
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelUpload}
        maxWidth="sm"
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Replace Existing Data?</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            This will delete all existing schedule data and replace it with data
            from the uploaded Excel file.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            The following data will be replaced:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText
                primary={`${classCount} classes (and their preferences)`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`${roomCount} rooms (and their availability)`}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={`${teacherCount} teachers (and their availability & specializations)`}
              />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>This action cannot be undone.</strong> Make sure you have
              downloaded a backup of your current data before proceeding.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpload}
            variant="contained"
            color="warning"
            startIcon={<CloudUploadIcon />}
          >
            Replace Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ScheduleExcelDialog;
